import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Typography } from 'antd';
import StatsKPIGroup, { KPIItem } from '../Business/StatsKPIGroup';
import { ForgeQueryBuilder, FieldConfig, ForgeQuery } from '../ForgeQueryBuilder';



type SectionConfig<TRow, TStats = any> = {
  key: string;
  title?: React.ReactNode;
  extra?: React.ReactNode;
  render: (ctx: { rows: TRow[]; query?: ForgeQuery; stats?: TStats }) => React.ReactNode;
};

type DashboardProps<TRow, TRequest, TStats> = {
  title?: React.ReactNode;
  headerContent?: React.ReactNode;
  queryFields: FieldConfig[];
  fetcher: (params: TRequest) => Promise<{ rows: TRow[]; stats?: TStats }>;
  buildParams?: (q: ForgeQuery) => TRequest;
  defaultQuery?: ForgeQuery;
  kpiItems?: KPIItem[] | ((stats: TStats) => KPIItem[]);
  actions?: React.ReactNode;
  sections: SectionConfig<TRow, TStats>[];
};

const { Title } = Typography;

const Dashboard = <TRow, TRequest = any, TStats = any>(props: DashboardProps<TRow, TRequest, TStats>) => {
  const { title, headerContent, queryFields, fetcher, buildParams, defaultQuery, kpiItems, actions, sections } = props;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TRow[]>([]);
  const [stats, setStats] = useState<TStats | undefined>(undefined);
  const [lastQuery, setLastQuery] = useState<ForgeQuery | undefined>(defaultQuery);

  const doFetch = async (params: TRequest) => {
    setLoading(true);
    try {
      const result = await fetcher(params);
      setRows((result?.rows || []) as TRow[]);
      setStats(result?.stats);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载（可能在开发模式下触发两次，这里不做防抖，保持简洁由后端幂等保障）
  useEffect(() => {
    onSearch(defaultQuery || {});
  }, []);

  const kpis = useMemo(() => {
    if (!kpiItems) return undefined;
    return typeof kpiItems === 'function' ? kpiItems(stats as any) : kpiItems;
  }, [kpiItems, stats]);

  const onSearch = async (q: ForgeQuery) => {
    setLastQuery(q);
    const params = (buildParams ? buildParams(q) : (q as unknown as TRequest));
    await doFetch(params);
  };

  return (
    <div style={{ padding: 24 }}>
      {title ? <Title level={2}>{title}</Title> : null}
      {kpis && kpis.length ? <StatsKPIGroup items={kpis as any} /> : null}
      {headerContent}
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <ForgeQueryBuilder fields={queryFields} onSearch={onSearch} defaultQuery={defaultQuery} />
      </div>
      {actions ? <div style={{ marginTop: 12 }}>{actions}</div> : null}
      {sections.map((sec) => (
        <Card key={sec.key} title={sec.title} style={{ marginTop: 12 }} extra={sec.extra}>
          <Spin spinning={loading}>{sec.render({ rows, query: lastQuery, stats })}</Spin>
        </Card>
      ))}
    </div>
  );
};

export default Dashboard;
